/*
  RS cart sections: Dawn's cart code (add to cart, quick add, quantity, remove) and the RS cart
  scripts (voucher, change size, suggested products, multi buy, Searchanise bridge) ask Shopify to
  send the drawer and cart icon HTML with the cart answer (`sections`) and redraw from it.
  Shopify leaves that HTML out (`sections: null`) on a theme preview and in a browser session that
  has been in the admin / a theme preview, also on the live theme (docs/shopify-findings.md #12):
  the product is added or removed, but the drawer does not open, the cart count stays as it was and
  the drawer keeps old parts until the page is reloaded.
  When a cart answer (/cart/add, /cart/change, /cart/update) has no sections although the request
  asked for them, the same sections are read with the Section Rendering API (GET ?sections=, which
  works in those sessions) and put into the answer before the caller reads it. Answers that already
  have them are passed on untouched (every customer's case). Loaded by snippets/rs-cart-scripts.liquid.
*/
(() => {
  if (window.rsCartSections) return;
  window.rsCartSections = true;

  const nativeFetch = window.fetch;
  const CART_PATH = /\/cart\/(add|change|update)(\.js)?$/;
  const MAX_PER_REQUEST = 5; // Section Rendering API limit

  // Sections asked for in the request body: FormData (Dawn's product form) or JSON.
  const asked = (body) => {
    let sections = null;
    let url = null;
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      sections = body.get('sections');
      url = body.get('sections_url');
    } else if (typeof body === 'string') {
      try {
        const data = JSON.parse(body);
        sections = data.sections;
        url = data.sections_url;
      } catch (error) {
        return null;
      }
    }
    if (typeof sections === 'string') sections = sections.split(',');
    if (!Array.isArray(sections)) return null;
    sections = sections.map((id) => String(id).trim()).filter(Boolean);
    return sections.length ? { sections, url: url || window.location.pathname } : null;
  };

  // No "Accept: application/json" header: with it Shopify answers a collection or the cart address
  // with that resource's JSON (keys "collection", "items" …) and ignores ?sections= (1.72 preview).
  const readFrom = async (ids, path) => {
    const result = {};
    for (let i = 0; i < ids.length; i += MAX_PER_REQUEST) {
      const url = new URL(path, window.location.origin);
      url.searchParams.set('sections', ids.slice(i, i + MAX_PER_REQUEST).join(','));
      const response = await nativeFetch(url.toString());
      if (!response.ok) throw new Error(response.status);
      Object.assign(result, await response.json());
    }
    return result;
  };

  // From the page the request names (as Shopify would), then from the home page for anything
  // still missing; a section that cannot be read makes the whole answer go back unchanged.
  const readSections = async (ids, path) => {
    const result = await readFrom(ids, path);
    const left = ids.filter((id) => result[id] == null);
    if (left.length) Object.assign(result, await readFrom(left, (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'));
    if (ids.some((id) => result[id] == null)) throw new Error('sections not found');
    return result;
  };

  const complete = async (response, request) => {
    if (!response.ok) return response;
    let data;
    try {
      data = await response.clone().json();
    } catch (error) {
      return response;
    }
    if (!data || typeof data !== 'object') return response;
    const missing = request.sections.filter((id) => !data.sections || data.sections[id] == null);
    if (!missing.length) return response;
    try {
      const read = await readSections(missing, request.url);
      data.sections = Object.assign({}, data.sections || {}, read);
    } catch (error) {
      return response; // as before: the caller gets Shopify's answer
    }
    return new Response(JSON.stringify(data), {
      status: response.status,
      statusText: response.statusText,
      headers: { 'Content-Type': 'application/json' },
    });
  };

  window.fetch = function (input, init) {
    const result = nativeFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : input && input.url;
      if (!url || !init || !init.body) return result;
      if (!CART_PATH.test(new URL(url, window.location.href).pathname)) return result;
      const request = asked(init.body);
      if (!request) return result;
      return result.then((response) => complete(response, request));
    } catch (error) {
      return result;
    }
  };
})();
